// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {isAbortError} from '@app/features/auth/state/SudoPrompt';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import styles from '@app/features/guild/components/modals/TransferOwnershipModal.module.css';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import type {User} from '@app/features/user/models/User';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

const logger = new Logger('TransferOwnershipModal');
export const TransferOwnershipModal: React.FC<{
	guildId: string;
	targetUser: User;
	targetMember: GuildMember;
}> = observer(({guildId, targetUser}) => {
	const {t} = useLingui();
	const [isTransferring, setIsTransferring] = useState(false);
	const handleTransfer = async () => {
		setIsTransferring(true);
		try {
			await GuildCommands.transferOwnership(guildId, targetUser.id);
			ToastCommands.createToast({
				type: 'success',
				children: <Trans>Successfully transferred ownership to {targetUser.username}</Trans>,
			});
			ModalCommands.pop();
		} catch (error) {
			if (isAbortError(error)) {
				return;
			}
			logger.error('Failed to transfer ownership:', error);
			ToastCommands.createToast({
				type: 'error',
				children: <Trans>Failed to transfer ownership. Please try again.</Trans>,
			});
		} finally {
			setIsTransferring(false);
		}
	};
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Transfer Community Ownership`} />
			<Modal.Content>
				<div className={styles.content}>
					<div className={styles.warningBox}>
						<p className={styles.warningText}>
							<Trans>
								You are about to transfer ownership of this community to <strong>{targetUser.username}</strong>. This
								action is <strong>irreversible</strong> and you will lose all owner privileges.
							</Trans>
						</p>
					</div>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()} disabled={isTransferring}>
					<Trans>Cancel</Trans>
				</Button>
				<Button variant="danger-primary" onClick={handleTransfer} disabled={isTransferring}>
					<Trans>Transfer Ownership</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
